import PrintIcon from '@mui/icons-material/Print'
import { Box, Card, Grid, Typography, useTheme } from '@mui/material'
import React, { Fragment, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import CCLogo from '../../../../Images/Branding/cc-combination-blue.png'
import { useUser } from '../../../../UserContext'
import { paymentsApi } from '../../../../api/swagger'
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import { useMountEffect } from '../../../../hooks/useMountEffect'
import { FetchBillingResponse } from '../../../../swagger'
import fullName from '../../../../utils/fullName'
import IconTextButton, {
  IconTextButtonVariant,
} from '../../../Buttons/IconTextButton'
import CardFormHeader from '../../../Card/CardFormHeader'
import CircularProgressOverlay from '../../../Elements/CircularProgressOverlay'
import DynamicBreadcrumbs, {
  Breadcrumb,
} from '../../../Elements/DynamicBreadcrumbs'
import InvoiceSignaturePreview from '../../invoiceProfile/InvoiceSignaturePreview'
import { TutorLicensingBreakdown } from './TutorLicensingBreakdown'

export const baseAccountBillingPath = '/account/billing'

const TutorLicensingFeeReport: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { user } = useUser()
  const { handleError } = useAppSnackbar()

  const [billingData, setBillingData] = useState<FetchBillingResponse>()

  const {
    BillingTab: { fetchBilling: fetchBillingLoadingId },
  } = useLoadingIds()

  const defaultErrorMessage = t(
    'TutorLicenseFeeReport.Error.FetchingBilling',
    'There was an error fetching the billing data.'
  )
  const breadcrumbs: Breadcrumb[] = [
    {
      label: t('Billing.Breadcrumb.Billing', 'Billing'),
      onClick: () => navigate(baseAccountBillingPath),
    },

    {
      label: t('Billing.BreadCrumb.TutorLicenseReport', 'Tutor License Report'),
    },
  ]

  const fetchBilling = async () => {
    try {
      const billingResponse = await paymentsApi.fetchBilling({
        orderBy: ['begunAt DESC'],
      })

      setBillingData(billingResponse)
    } catch (e) {
      await handleError(e, defaultErrorMessage)
    }
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchBilling,
    loadingId: fetchBillingLoadingId,
  })

  useMountEffect(triggerFetch)

  const printInvoice = () => {
    window.print()
  }

  if (isLoading) {
    return <CircularProgressOverlay isLoading />
  }

  const LicensingFeeReportHeader = (
    <Box role="heading" aria-label="licensingFeeReportHeader">
      <Grid container justifyContent="space-between">
        <Grid item xs={4} sm={6}>
          <Box
            sx={{
              '@media print': {
                maxWidth: '150px',
              },
            }}
          >
            <img
              style={{
                height: 'auto',
                width: '100%',
                maxWidth: '150px',
              }}
              src={CCLogo}
              alt={t('Invoice.Logo', 'Classical Conversations Logo')}
            />
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="h5"
        sx={{
          marginTop: theme.spacing(2),
          marginBottom: 0,
        }}
      >
        {t('TutorLicensingFeeReport.Heading1', 'License for {{tutorName}}', {
          tutorName: fullName(user),
        })}
      </Typography>
    </Box>
  )

  const PageButtons = (
    <>
      <IconTextButton
        id="printLicensingFeeReport"
        variant={IconTextButtonVariant.Print}
        startIcon={<PrintIcon />}
        onClick={printInvoice}
      />
    </>
  )

  return (
    <Box>
      <DynamicBreadcrumbs breadcrumbs={breadcrumbs} />
      <Card
        sx={{
          padding: theme.spacing(3, 4, 4),
          maxWidth: 1200,
          color: theme.palette.primary.main,
        }}
      >
        <CardFormHeader header={<></>} buttons={PageButtons} />

        {LicensingFeeReportHeader}

        <Grid container>
          <Grid item xs={12} md={12}>
            {billingData?.licensing?.programs?.map((licensingBill) => (
              <Fragment key={licensingBill._for.programKey}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    marginBottom: theme.spacing(2),
                  }}
                >
                  {t(
                    'TutorLicensingFeeReport.CommunityName',
                    '{{communityName}} Community',
                    {
                      communityName: licensingBill._for.communityName || '',
                    }
                  )}
                </Typography>
                <TutorLicensingBreakdown
                  key={licensingBill._for.programKey}
                  licensingBill={licensingBill}
                  licensingHistory={billingData?.licensing?.history || []}
                />
                <Box
                  sx={{
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(2),
                  }}
                />
              </Fragment>
            ))}

            <Box role="region" aria-label="invoiceFooter">
              <InvoiceSignaturePreview
                invoiceSignature={{
                  payableTo:
                    billingData?.licensing?.invoiceSignature?.payableTo ?? '',
                  address: billingData?.licensing?.invoiceSignature?.address,
                  closingMessage:
                    billingData?.licensing?.invoiceSignature?.closingMessage,
                  phoneNumber:
                    billingData?.licensing?.invoiceSignature?.phoneNumber,
                  email: billingData?.licensing?.invoiceSignature?.email,
                  isViewingInvoicePreview: false,
                  qrCodeImage:
                    billingData?.licensing?.invoiceSignature?.qrCodeImage,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Box>
  )
}

export default TutorLicensingFeeReport
